import { request } from "@umijs/max";
import { abortControllers, AbortControllerType } from "./constants";
import {
  WecomConversationDataType,
  WecomInnerContact,
  WecomMessageType,
  WecomOuterContact,
  WS_REQUEST_MESSAGE_TYPE,
  WecomRoom,
  ServiceType,
  ChannelTypeEnum,
} from "@packages/chat-sdk";
import { nanoid } from "nanoid";

/**
 * 获取置顶会话列表
 *
 */
export type GetTopConversationListParams = {
  fromUserId?: string;
  teamId?: string;
  name?: string;
  unRead?: boolean;
  robotStatus?: boolean;
};
export function getConversationTopListService(
  params: GetTopConversationListParams
) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.CONVERSATION_TOP_LIST
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.CONVERSATION_TOP_LIST, controller);

  return request<API.BaseResponse<WecomConversationDataType[]>>(
    "/api/damselfish-chat-bot/conversation/topList",
    {
      method: "GET",
      signal: controller.signal,
      params: {
        ...params,
        channelType: ChannelTypeEnum.Wecom,
      },
    }
  );
}

/**
 * 获取会话列表
 *
 */

export type GetConversationListParams = {
  fromUserId?: string;
  teamId?: string;
  name?: string;
  unRead?: boolean;
  pageNo?: number;
  pageSize?: number;
  robotStatus?: boolean;
};

export function getConversationListService(params: GetConversationListParams) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.CONVERSATION_LIST
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.CONVERSATION_LIST, controller);
  return request<
    API.BaseResponse<{ list: WecomConversationDataType[]; total: number }>
  >("/api/damselfish-chat-bot/conversation/page", {
    method: "GET",
    signal: controller.signal,
    params: {
      ...params,
      channelType: ChannelTypeEnum.Wecom,
    },
  });
}

/**
 * 一键清除未读消息
 * */
export function clearUnreadService(teamId?: string) {
  return request<API.BaseResponse<boolean>>(
    "/api/damselfish-chat-bot/conversation/unread",
    {
      method: "POST",
      params: {
        teamId,
        channelType: ChannelTypeEnum.Wecom,
      },
    }
  );
}

/**
 *
 * 更改会话状态
 */
type ToggleConversationStatusParams = {
  chat_id: string;
  is_top?: number;
  is_shield?: number;
  unread?: number;
};
type ToggleConversationStatusData = {
  data: {
    chat_id: string;
    is_top?: number;
    is_shield?: number;
    unread?: number;
  };
};

export function toggleConversationStatusService(
  fromUserId: string,
  params: ToggleConversationStatusParams
) {
  return request<API.BaseResponse<ToggleConversationStatusData>>(
    "/api/damselfish-chat-bot/command?UPDATE_CONVERSATION_STATUS",
    {
      method: "POST",
      data: {
        message_type: WS_REQUEST_MESSAGE_TYPE.UPDATE_CONVERSATION_STATUS,
        data: params,
        user_id: fromUserId,
        channel_type: ChannelTypeEnum.Wecom,
      },
    }
  );
}

/**
 * 创建会话
 *
 */
type CreateConversationParams = {
  userId: string;
  otherId: string;
};
export function createConversationService(params: CreateConversationParams) {
  return request<API.BaseResponse<WecomConversationDataType>>(
    "/api/damselfish-chat-bot/conversation/create",
    {
      method: "POST",
      data: {
        ...params,
        channelType: ChannelTypeEnum.Wecom,
      },
    }
  );
}

/**
 * 查询会话
 */
type FindConversationParams = {
  userId: string;
  conversationId: string;
};
export function findConversationService(params: FindConversationParams) {
  return request<API.BaseResponse<WecomConversationDataType>>(
    "/api/damselfish-chat-bot/conversation/findById",
    {
      method: "POST",
      data: {
        ...params,
        channelType: ChannelTypeEnum.Wecom,
      },
    }
  );
}

/**
 * 查询外部联系人详情
 */
export function getExternalContactDetailService(
  userId: string,
  fromUserId?: string
) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.OUTER_CONTACT_DETAIL
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.OUTER_CONTACT_DETAIL, controller);
  return request<API.BaseResponse<WecomOuterContact>>(
    "/api/damselfish-chat-bot/user/detail",
    {
      method: "GET",
      signal: controller.signal,
      params: {
        userId,
        fromUserId,
        channelType: ChannelTypeEnum.Wecom,
      },
    }
  );
}

/**
 *
 * 获取内部联系人详情
 */
export function getInternalContactDetailService(
  userId: string,
  fromUserId: string
) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.INNER_CONTACT_DETAIL
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.INNER_CONTACT_DETAIL, controller);
  return request<API.BaseResponse<WecomInnerContact>>(
    "/api/damselfish-chat-bot/user/detail",
    {
      method: "GET",
      signal: controller.signal,
      params: {
        userId,
        fromUserId,
        channelType: ChannelTypeEnum.Wecom,
      },
    }
  );
}

/**
 * 查询群详情
 */
type GetRoomDetailParams = {
  userId: string;
  roomId: string;
};
export function getRoomDetailService(params: GetRoomDetailParams) {
  const controller = new AbortController();
  const oldController = abortControllers.get(AbortControllerType.ROOM_DETAIL);
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.ROOM_DETAIL, controller);
  return request<API.BaseResponse<WecomRoom>>(
    "/api/damselfish-chat-bot/room/detail",
    {
      method: "GET",
      signal: controller.signal,
      params: {
        ...params,
        channelType: ChannelTypeEnum.Wecom,
      },
    }
  );
}

/**
 *
 * 获取历史消息
 */
export function getHistoryMessageListService(data: {
  chatId: string;
  lastMsgTime?: string;
  pageNo?: number;
  pageSize?: number;
}) {
  const controller = new AbortController();
  const oldController = abortControllers.get(
    AbortControllerType.MESSAGE_HISTORY
  );
  if (oldController) {
    oldController.abort();
  }
  abortControllers.set(AbortControllerType.MESSAGE_HISTORY, controller);
  return request<
    API.BaseResponse<{
      records: WecomMessageType[];
      total: number;
    }>
  >("/api/damselfish-chat-bot/history/page", {
    method: "POST",
    signal: controller.signal,
    data: {
      ...data,
      channelType: ChannelTypeEnum.Wecom,
    },
  });
}

/**
 * 添加被动删除的好友
 */
export type AddPassiveDeletedParams = {
  user_id: string;
  verify_text: string;
};

export function addPassiveDeletedFriendService(
  from_user_id: string,
  service_type: ServiceType,
  data: AddPassiveDeletedParams
) {
  return request<API.BaseResponse<boolean>>(
    "/api/damselfish-chat-bot/command?ADD_FRIEND",
    {
      method: "POST",
      data: {
        user_id: from_user_id,
        service_type,
        message_type: WS_REQUEST_MESSAGE_TYPE.ADD_FRIEND,
        data,
        request_key: nanoid(),
        channel_type: ChannelTypeEnum.Wecom,
      },
    }
  );
}

export type AddFriendByBusinessCardParams = {
  /** 如果来自群，则需要填入群ID，不需要带 R:; 如果来自好友，这个字段填0就行(后端说不用传） */
  from_room_id?: string;
  /** 推荐者ID */
  presenter_id: string;
  /** 添加的ID */
  user_id: string;
  /** 验证文本 */
  verify_text: string;
};

/** 添加名片分享的好友 */
export function addFriendByBusinessCardService(
  from_user_id: string,
  service_type: ServiceType,
  data: AddFriendByBusinessCardParams
) {
  return request<API.BaseResponse<boolean>>(
    "/api/damselfish-chat-bot/command?ADD_FRIEND_BY_BUSINESS_CARD",
    {
      method: "POST",
      data: {
        user_id: from_user_id,
        service_type,
        message_type: WS_REQUEST_MESSAGE_TYPE.ADD_FRIEND_BY_BUSINESS_CARD,
        data,
        request_key: nanoid(),
        channel_type: ChannelTypeEnum.Wecom,
      },
    }
  );
}

export enum KnowledgeSourceTypeEnum {
  AI = "AI",
  RG_REPLY = "RG_REPLY",
}
export enum KnowledgeRectifyTypeEnum {
  POSITIVE = "POSITIVE",
  NEGATIVE = "NEGATIVE",
}
export type AddKnowledgeDto = {
  sourceType: KnowledgeSourceTypeEnum;
  reply: string;
  question: string;
  rectifyType: KnowledgeRectifyTypeEnum;
  robotId: string;
  msgId: string;
  taskId: string;
  chatId: string;
};
export function addKnowledgeService(data: Partial<AddKnowledgeDto>) {
  return request<API.BaseResponse<Record<string, string>>>(
    "/api/damselfish-chat-bot/history/msgOperate",
    {
      method: "POST",
      data,
    }
  );
}

export function cancelKnowledgeService(
  params: Partial<{
    robotTaskId: string;
  }>
) {
  return request<API.BaseResponse<string[]>>(
    "/api/damselfish-chat-bot/history/removeMsgOperate",
    {
      method: "POST",
      params,
    }
  );
}
